'use client'

/**
 * Navbar notification bell — unread badge + dropdown. Surfaces follows, DMs, @mentions, and
 * price alerts from the notifications table. Polls the unread count; loads the list on open;
 * marks everything read when opened. This is what makes the site's social events visible.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { Bell, Loader2, UserPlus, MessageSquare, AtSign, TrendingUp, LifeBuoy, Filter, Gift, Newspaper, Smartphone, SlidersHorizontal, Zap } from 'lucide-react'
import UserAvatar from '@/components/UserAvatar'
import PushNotificationToggle from '@/components/PushNotificationToggle'
import DailyBriefToggle from '@/components/DailyBriefToggle'
// ⚠ ONE COPY, shared with /notifications. Two switches on two surfaces is how one of them
// silently stops linking a type the other handles - which is exactly how x_mention stayed dead.
import { hrefFor, isExternal } from '@/lib/notifications/notificationLink'

type Notif = {
  id: string; type: string; message: string; isRead: boolean; relatedId: string | null; ms: number
  actorUsername: string | null; actorName: string | null; actorImage: string | null; actorDonorTier: string | null; actorLevel: number | null
}

const timeAgo = (ms: number) => {
  if (!ms) return ''
  const s = Math.max(1, Math.floor((Date.now() - ms) / 1000))
  if (s < 60) return `${s}s`; const m = Math.floor(s / 60); if (m < 60) return `${m}m`
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`; return `${Math.floor(h / 24)}d`
}


/**
 * ⚠ NOT `router.push` FOR EVERYTHING. A notification can point off-site (the Reddit mod queue, the
 * Reddit inbox), and `router.push` on an absolute URL asks the Next router for a client route that
 * does not exist. Off-site opens in a new tab so the site is not lost behind it.
 */
function navigate(router: ReturnType<typeof useRouter>, href: string): void {
  if (isExternal(href)) window.open(href, '_blank', 'noopener,noreferrer')
  else router.push(href)
}
function IconFor({ type }: { type: string }) {
  const cls = 'h-3.5 w-3.5'
  if (type === 'follow') return <UserPlus className={`${cls} text-amber-400`} />
  if (type === 'message') return <MessageSquare className={`${cls} text-sky-400`} />
  if (type === 'mention' || type === 'mention_post') return <AtSign className={`${cls} text-violet-400`} />
  if (type === 'price_alert') return <TrendingUp className={`${cls} text-emerald-400`} />
  if (type === 'screen_match') return <Filter className={`${cls} text-amber-400`} />
  // Match app/notifications/page.tsx exactly - the full-page list already gives watchlist_alert an
  // amber TrendingUp, but this bell/toast IconFor never got the case, so the product's most-read alert
  // showed the anonymous gray default on the one surface ~155 of 158 recipients actually see (only 3
  // hold a push subscription). Same one-surface-updated-the-other-didn't drift the shared hrefFor exists to kill.
  if (type === 'watchlist_alert') return <TrendingUp className={`${cls} text-amber-400`} />
  if (type === 'conviction_alert') return <Zap className={`${cls} text-amber-300`} />
  if (type === 'support') return <LifeBuoy className={`${cls} text-rose-400`} />
  if (type === 'referral') return <Gift className={`${cls} text-emerald-400`} />
  if (type === 'article') return <Newspaper className={`${cls} text-amber-400`} />
  if (type === 'comment') return <MessageSquare className={`${cls} text-amber-400`} />
  if (type === 'x_mention') return <AtSign className={`${cls} text-sky-400`} />
  if (type === 'reddit_modqueue' || type === 'reddit_comment' || type === 'reddit_reply') return <MessageSquare className={`${cls} text-orange-400`} />
  return <Bell className={`${cls} text-muted-foreground`} />
}

export default function NotificationBell() {
  const { status } = useSession()
  const router = useRouter()
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<Notif[] | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  /**
   * ⭐ IN-APP POPUP. Owner 2026-08-08: "double check that users receive popup notifications ...
   * best in class smooth experiences".
   *
   * Before this, a new DM or support reply changed a small number on a bell and nothing else. If you
   * were not looking at the top-right corner you never knew. Browser push covers the case where you
   * are AWAY from the site, but only 4 users have granted permission, and push does not fire for the
   * tab you are actively using. This covers the common case: you are on the site, right now.
   *
   * ⚠ SEEDED, NOT ZEROED. `lastUnread` starts at null and the first poll only records the baseline.
   * Starting it at 0 would fire a toast for every unread notification you already had, every single
   * page load, which is the opposite of smooth.
   */
  const lastUnread = useRef<number | null>(null)
  const [toast, setToast] = useState<Notif | null>(null)

  const pollCount = useCallback(async () => {
    if (document.visibilityState !== 'visible') return
    try {
      const r = await fetch('/api/notifications?count=1', { cache: 'no-store' })
      if (!r.ok) return
      const n = (await r.json()).unread ?? 0
      setUnread(n)
      const prev = lastUnread.current
      lastUnread.current = n
      // Only an INCREASE is news. A decrease means they read something elsewhere.
      if (prev !== null && n > prev) {
        try {
          const list = await fetch('/api/notifications', { cache: 'no-store' })
          if (list.ok) {
            const items: Notif[] = (await list.json()).notifications ?? []
            const newest = items.find((i) => !i.isRead) ?? items[0]
            if (newest) {
              setToast(newest)
              // Long enough to read and click, short enough not to sit in the way.
              window.setTimeout(() => setToast((t) => (t?.id === newest.id ? null : t)), 8000)
            }
          }
        } catch { /* the badge already updated; the toast is a bonus */ }
      }
    } catch { /* noop */ }
  }, [])
  useEffect(() => {
    if (status !== 'authenticated') return
    // 15s, not 30s: a direct message taking half a minute to appear does not read as live. One small
    // indexed count query per user per 15s while the tab is VISIBLE (the poll returns early otherwise).
    pollCount(); const iv = setInterval(pollCount, 15_000)
    return () => clearInterval(iv)
  }, [status, pollCount])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggle = async () => {
    const next = !open; setOpen(next)
    if (next) {
      setItems(null)
      try {
        const r = await fetch('/api/notifications', { cache: 'no-store' })
        if (r.ok) { const j = await r.json(); setItems(j.notifications ?? []) }
      } catch { setItems([]) }
      // Mark all read on open.
      if (unread > 0) { fetch('/api/notifications', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }).catch(() => {}); setUnread(0) }
    }
  }

  if (status !== 'authenticated') return null
  return (
    <div ref={wrapRef} className="relative">
      {/* The popup itself. Fixed to the viewport rather than anchored to the bell so it cannot be
          clipped by the navbar's overflow, and bottom-right on mobile where a top toast would sit
          under the notch. Clicking it goes straight to the thing that happened. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-[200] w-[min(22rem,calc(100vw-2rem))] animate-in slide-in-from-bottom-2 fade-in duration-200 sm:bottom-6 sm:right-6"
        >
          <button
            type="button"
            onClick={() => { const h = hrefFor(toast); setToast(null); if (h) navigate(router, h) }}
            className="flex w-full items-start gap-2.5 rounded-xl border border-border bg-background/95 p-3 text-left shadow-2xl backdrop-blur transition hover:border-amber-500/50 hover:bg-card"
          >
            <span className="mt-0.5 shrink-0 rounded-lg bg-card p-1.5 ring-1 ring-border">
              <IconFor type={toast.type} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-snug text-foreground">{toast.message}</span>
              <span className="mt-0.5 block text-[11px] text-muted-foreground">just now, tap to open</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              aria-label="Dismiss"
              onClick={(e) => { e.stopPropagation(); setToast(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setToast(null) } }}
              className="shrink-0 rounded px-1.5 py-0.5 text-lg leading-none text-muted-foreground transition hover:text-foreground"
            >
              &times;
            </span>
          </button>
        </div>
      )}

      <button onClick={toggle} title="Notifications" className="relative flex items-center rounded-lg px-2 py-2 transition-colors hover:bg-accent/50">
        <Bell className="h-5 w-5 text-foreground" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[130] mt-2 w-80 overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-xs font-bold uppercase tracking-wider text-foreground">Notifications</span>
            <PushNotificationToggle />
          </div>
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <DailyBriefToggle />
          </div>
          {/* Setup helper. The two toggles above turn alerts on; this explains how to actually receive
              them on a phone (iOS needs the home-screen install, and push only fires for chosen alerts).
              Routed through the same navigate() + setOpen(false) as the notification rows below. */}
          <button
            onClick={() => { setOpen(false); navigate(router, '/guides/mobile-alerts') }}
            className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-[12px] font-medium text-muted-foreground transition-colors hover:bg-card hover:text-amber-300"
          >
            <Smartphone className="h-3.5 w-3.5 shrink-0" />
            How to get alerts on your phone
          </button>
          {/* The controls for WHICH alerts you get already exist but were buried in the /alerts filter
              bar; this is the findable entry to the dedicated preferences page. */}
          <button
            onClick={() => { setOpen(false); navigate(router, '/alerts/settings') }}
            className="flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left text-[12px] font-medium text-muted-foreground transition-colors hover:bg-card hover:text-amber-300"
          >
            <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
            Tune which alerts you get
          </button>
          <div className="max-h-[400px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {items === null ? (
              <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">No notifications yet. Follow some traders to get started.</p>
            ) : items.map((n) => {
              const href = hrefFor(n)
              const inner = (
                <div className={`flex items-start gap-2.5 px-3 py-2.5 transition-colors ${href ? 'cursor-pointer hover:bg-card' : ''} ${!n.isRead ? 'bg-amber-500/[0.04]' : ''}`}>
                  {n.actorUsername ? (
                    <UserAvatar src={n.actorImage} name={n.actorName || n.actorUsername} size={30} donorTier={n.actorDonorTier} level={n.actorLevel} />
                  ) : (
                    <span className="mt-0.5 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-muted"><IconFor type={n.type} /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5"><IconFor type={n.type} /><span className="truncate text-sm text-foreground">{n.message}</span></div>
                    <span className="text-[11px] text-muted-foreground">{timeAgo(n.ms)}</span>
                  </div>
                  {!n.isRead && <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-amber-400" />}
                </div>
              )
              return href
                ? <button key={n.id} onClick={() => { setOpen(false); navigate(router, href) }} className="block w-full text-left">{inner}</button>
                : <div key={n.id}>{inner}</div>
            })}
          </div>
          {/*
            ⚠ THE DROPDOWN SHOWS 40 ROWS AND TRUNCATES EVERY ONE. Without this link the older
            alerts are unreachable and a long X reply can only be read by leaving the site.
            Added 2026-08-23 with the /notifications page it points at.
          */}
          <button
            onClick={() => { setOpen(false); navigate(router, '/notifications') }}
            className="block w-full border-t border-border px-3 py-2.5 text-center text-xs font-semibold text-amber-400 transition-colors hover:bg-card"
          >
            See all notifications
          </button>
        </div>
      )}
    </div>
  )
}
